// Harness FME (Split.io) feature-flag plumbing.
//
// MIGRATION PREP ONLY: this module adds flag-evaluation plumbing and defaults
// every flag to OFF, so production behavior is unchanged until a flag is turned
// on in Harness FME. It does NOT repoint or migrate any endpoint.
//
// Primary mechanism is the synchronous `isEnabled(flagKey)` accessor, intended
// for use inside `services.js` files to choose between the legacy Ruby gateway
// and the FastAPI gateway, e.g.:
//
//   (isEnabled("migrate_<component>_<group>") ? fcaFastApi : fcaGateWay).get(path)
//
// Fail-safe behavior: if the SDK key is missing, the client is not ready, or the
// SDK returns the "control" treatment, `isEnabled` returns the provided default
// (OFF by default) so the app keeps using the old backend.
//
// Because that fallback is silent, `isEnabled` is only trustworthy once flag
// definitions have arrived. `whenFlagsReady()` exposes that moment so app
// bootstrap can wait for it; without the wait, the first requests of a session
// evaluate every flag as OFF and reach the legacy gateway despite being ON.
//
// Two further failure modes make a flag flip fail to reach a browser, both of
// which survive a hard reload because they live in localStorage / the network
// path rather than the HTTP cache:
//
//   1. Stale definitions. Definitions persist in localStorage under the
//      `FCA_FME.SPLITIO.*` prefix and are only discarded when the SDK key /
//      flag filter / spec version changes or after `expirationDays`. If the
//      cached global watermark (`splits.till`) is already at or past a flag's
//      change number while the cached definition is older, the SDK's delta
//      fetch returns "no changes" and the stale treatment is served
//      indefinitely, across any number of reloads. `clearOnInit` bounds that.
//   2. Unreachable FME. If `sdk.split.io` is blocked (corporate proxy, content
//      blocker, CSP) every treatment resolves to "control" and silently fails
//      open to the legacy gateway forever. `getFlagDiagnostics()` surfaces it.

import { SplitFactory } from "@splitsoftware/splitio";
import { FME_SDK_KEY, FME_ENV } from "../config/constants";

const TRAFFIC_TYPE = "user";

// Upper bound on how long app bootstrap waits for the SDK to deliver flag
// definitions. Until the SDK is ready `isEnabled()` fails open to OFF, so any
// flag-gated request issued before then silently goes to the legacy Ruby
// gateway even when the flag is ON in Harness FME. Waiting here closes that
// window; the cap keeps first paint bounded if FME is slow or unreachable.
const READY_GATE_TIMEOUT_MS = 2000;

// localStorage prefix for cached flag definitions. Kept in sync with the
// `storage.prefix` below so diagnostics can read the same keys the SDK writes.
const STORAGE_PREFIX = "FCA_FME";

let factory = null;
let client = null;
let isReady = false;
let initStarted = false;
let readyGate = null;

// How definitions were obtained. "network" is authoritative; "cache" means we
// are serving possibly-stale definitions; "timeout"/"error"/"disabled" mean
// every flag is failing open to its default (OFF -> legacy gateway).
let readySource = null;
const sdkEvents = [];

// Flags already reported as unresolvable, so a broken FME path is logged once
// per flag instead of on every evaluation.
const reportedControl = new Set();

const recordEvent = name => {
    sdkEvents.push({ event: name, atMs: Math.round(typeof performance !== "undefined" ? performance.now() : Date.now()) });
};

/**
 * Build the current Split evaluation key + attributes from localStorage.
 * Re-read on every evaluation so role/client/identity changes are reflected
 * without re-initializing the SDK.
 */
const getCurrentKey = () => {
    try {
        return localStorage.getItem("userId") || "anonymous";
    } catch (_) {
        return "anonymous";
    }
};

const getCurrentAttributes = () => {
    try {
        return {
            role: localStorage.getItem("role") || null,
            clientId: localStorage.getItem("clientId") || null,
            consultancyId: localStorage.getItem("consultancy_id") || null,
            email: localStorage.getItem("userMail") || null,
            env: FME_ENV
        };
    } catch (_) {
        return { env: FME_ENV };
    }
};

/**
 * Initialize the Split factory + client exactly once. Safe to call multiple
 * times. Returns the same promise as `whenFlagsReady()` so bootstrap can wait
 * for flag definitions before issuing flag-gated requests.
 */
export const initFeatureFlags = () => {
    if (initStarted) {
        return readyGate || Promise.resolve();
    }
    initStarted = true;

    let settle = () => {};
    readyGate = new Promise(resolve => {
        let settled = false;
        settle = () => {
            if (!settled) {
                settled = true;
                resolve();
            }
        };
    });

    if (!FME_SDK_KEY) {
        // No SDK key configured (e.g. local/dev or env without a key injected).
        // Stay fail-safe: every flag resolves to its default (OFF).
        console.warn("[featureFlags] FME_SDK_KEY is empty; feature flags disabled (fail-safe OFF).");
        readySource = "disabled";
        recordEvent("NO_SDK_KEY");
        settle();
        return readyGate;
    }

    if (!FME_ENV) {
        // The SDK key still selects the environment, so evaluation proceeds. But an
        // unnamed environment means this deployment is missing its Harness FME
        // override, and nothing in the browser then says which environment's flag
        // definitions are in force.
        console.warn("[featureFlags] FME_SDK_KEY is set but FME_ENV is empty; this deployment is missing its Harness FME environment override.");
        recordEvent("NO_FME_ENV");
    }

    // Never let a slow or unreachable FME endpoint hold up first paint.
    setTimeout(settle, READY_GATE_TIMEOUT_MS);

    try {
        factory = SplitFactory({
            core: {
                authorizationKey: FME_SDK_KEY,
                key: getCurrentKey(),
                trafficType: TRAFFIC_TYPE
            },
            startup: {
                // Short timeout so a slow/unreachable CDN never delays evaluations;
                // until ready, isEnabled() falls back to defaults (OFF).
                readyTimeout: 5
            },
            storage: {
                // Cache last-known treatments in localStorage for resilience across
                // reloads and brief network outages.
                type: "LOCALSTORAGE",
                prefix: STORAGE_PREFIX,
                // Discard the cache on the first init of each 24h period. Without
                // this, a cache whose watermark is at/past a flag's change number
                // never refetches that definition, so a flag flip can never reach
                // the browser — no reload, hard reload, or redeploy clears it.
                // Cost is one cold flag fetch per browser per day, bounded by
                // READY_GATE_TIMEOUT_MS.
                clearOnInit: true,
                expirationDays: 1
            },
            // Keep the SDK quiet in production console.
            debug: false
        });

        client = factory.client();

        client.on(client.Event.SDK_READY, () => {
            isReady = true;
            readySource = "network";
            recordEvent("SDK_READY");
            reportedControl.clear();
            console.info("[featureFlags] Split SDK ready.");
            settle();
        });

        client.on(client.Event.SDK_READY_FROM_CACHE, () => {
            // Cached definitions may predate a flag flip. Do not set `isReady` or
            // release the bootstrap gate here — wait for SDK_READY / SDK_UPDATE so
            // routing reflects the current FME state, not stale localStorage.
            if (readySource === null) {
                readySource = "cache";
            }
            recordEvent("SDK_READY_FROM_CACHE");
            console.info("[featureFlags] Split SDK ready from cache (awaiting network).");
        });

        client.on(client.Event.SDK_UPDATE, () => {
            // Definitions changed; subsequent getTreatment calls use fresh data.
            // Routing is evaluated per request, so in-flight sessions pick this
            // up on their next call without a reload.
            isReady = true;
            readySource = "network";
            recordEvent("SDK_UPDATE");
            reportedControl.clear();
            console.info("[featureFlags] Split SDK definitions updated.");
        });

        client.on(client.Event.SDK_READY_TIMED_OUT, () => {
            // FME is unreachable. If nothing came from cache either, every flag
            // is now failing open to OFF -> legacy gateway, for the whole
            // session, and will keep doing so on every reload until the network
            // path is fixed. Warn loudly: this is silent misrouting otherwise.
            if (readySource === null) {
                readySource = "timeout";
            }
            recordEvent("SDK_READY_TIMED_OUT");
            console.warn(
                "[featureFlags] Split SDK could not reach Harness FME; flag-gated routing is " +
                    "falling back to defaults (legacy backend). Check that sdk.split.io is reachable."
            );
            settle();
        });
    } catch (err) {
        // Never throw from init. Leave the client null so isEnabled() returns defaults.
        factory = null;
        client = null;
        isReady = false;
        readySource = "error";
        recordEvent("INIT_FAILED");
        console.warn("[featureFlags] Split SDK initialization failed; feature flags disabled (fail-safe OFF).", err);
        settle();
    }

    return readyGate;
};

/**
 * Resolves once flag definitions are available, or once the bootstrap wait is
 * capped / the SDK gives up — whichever happens first. Never rejects.
 *
 * Callers that route on a flag (see `isEnabled`) should let the app bootstrap
 * await this before issuing requests, otherwise early requests fail open to the
 * legacy Ruby gateway even when the flag is ON.
 */
export const whenFlagsReady = () => readyGate || Promise.resolve();

/**
 * Synchronous, fail-safe flag check.
 *
 * Returns `true` only when the Split client is ready AND the treatment for
 * `flagKey` is exactly "on". In every other case (no SDK key, client not ready,
 * "control" treatment from an error/unknown flag, or any thrown error) it
 * returns `defaultValue`, which defaults to `false` (OFF -> legacy backend).
 *
 * @param {string} flagKey - FME flag/split name, e.g. "migrate_assets_wave1".
 * @param {boolean} [defaultValue=false] - Returned when the flag can't be resolved.
 * @returns {boolean}
 */
export const isEnabled = (flagKey, defaultValue = false) => {
    if (!client || !isReady) {
        return defaultValue;
    }

    try {
        // The browser client is bound to `core.key` at init; attributes are
        // re-read on every call so role/client changes are reflected.
        const treatment = client.getTreatment(flagKey, getCurrentAttributes());
        if (treatment === "on") {
            return true;
        }
        if (treatment === "off") {
            return false;
        }
        // "control" (SDK not ready / error / unknown flag) -> fail-safe default.
        // This is the silent-misrouting case: the flag may well be ON in FME but
        // we cannot see it, so traffic keeps going to the legacy backend. Report
        // once per flag so it is diagnosable from the browser console.
        if (!reportedControl.has(flagKey)) {
            reportedControl.add(flagKey);
            console.warn(
                `[featureFlags] "${flagKey}" resolved to "control" (unresolvable); using default ` +
                    `${defaultValue}. Flag-gated routing for this flag is not in effect.`
            );
        }
        return defaultValue;
    } catch (err) {
        console.warn(`[featureFlags] getTreatment failed for "${flagKey}"; using default.`, err);
        return defaultValue;
    }
};

/**
 * True only when definitions were fetched from FME during this session. When
 * false, flag-gated routing is running on cached (possibly pre-flip) or default
 * treatments, so a flag flip may not be in effect yet.
 */
export const flagsAreAuthoritative = () => readySource === "network";

/**
 * Snapshot of everything needed to explain a routing decision, for support and
 * for verifying a cutover from a real browser session. Never throws.
 *
 * Reports the evaluation key and attributes actually used, how definitions were
 * obtained, the SDK event sequence with timings, the cached definition
 * watermark, and the live treatment for every flag the SDK knows about.
 */
export const getFlagDiagnostics = () => {
    const diagnostics = {
        env: FME_ENV,
        sdkKeyConfigured: Boolean(FME_SDK_KEY),
        readySource,
        isReady,
        authoritative: flagsAreAuthoritative(),
        evaluationKey: getCurrentKey(),
        attributes: getCurrentAttributes(),
        events: sdkEvents.slice(),
        cache: {},
        treatments: {}
    };

    try {
        // Cached watermark + last write time. A `lastUpdated` far in the past
        // with treatments that disagree with FME means stale definitions.
        const till = localStorage.getItem(`${STORAGE_PREFIX}.SPLITIO.splits.till`);
        const lastUpdated = localStorage.getItem(`${STORAGE_PREFIX}.SPLITIO.splits.lastUpdated`);
        diagnostics.cache = {
            till,
            lastUpdated,
            lastUpdatedISO: lastUpdated ? new Date(Number(lastUpdated)).toISOString() : null,
            definitionCount: Object.keys(localStorage).filter(k => k.startsWith(`${STORAGE_PREFIX}.SPLITIO.split.`)).length
        };
    } catch (err) {
        diagnostics.cache = { error: String(err) };
    }

    try {
        if (factory && client) {
            factory.manager().names().forEach(name => {
                diagnostics.treatments[name] = client.getTreatment(name, getCurrentAttributes());
            });
        }
    } catch (err) {
        diagnostics.treatments = { error: String(err) };
    }

    return diagnostics;
};

/**
 * Discard cached flag definitions. Use when a browser is stuck on pre-flip
 * treatments; the SDK refetches from FME on the next init (i.e. after reload).
 * Returns the number of keys removed.
 */
export const clearFlagCache = () => {
    try {
        const keys = Object.keys(localStorage).filter(k => k.startsWith(`${STORAGE_PREFIX}.SPLITIO`));
        keys.forEach(k => localStorage.removeItem(k));
        console.info(`[featureFlags] Cleared ${keys.length} cached flag entries; reload to refetch.`);
        return keys.length;
    } catch (err) {
        console.warn("[featureFlags] Could not clear flag cache.", err);
        return 0;
    }
};

/**
 * Advanced use only: returns the underlying Split client (or null if not
 * initialized). Prefer `isEnabled()` for routing decisions.
 */
export const getClient = () => client;

// Expose diagnostics on window so a misrouting report can be settled from the
// browser console without a debug build. Read-only helpers; no flag mutation.
try {
    if (typeof window !== "undefined") {
        window.__fcaFeatureFlags = {
            diagnostics: getFlagDiagnostics,
            isEnabled,
            clearCache: clearFlagCache
        };
    }
} catch (_) {
    // Non-browser environment (tests/SSR); diagnostics simply unavailable.
}
